/**
 * Ant Design ConfigProvider 테마 — Partner (Amber)
 *
 * 밀도(높이·패딩·글자 크기)의 정본은 **여기**다 (2026-09-03 밀도 키트 v3).
 * lab frontend/src/theme/index.ts 의 사본 — 브랜드 색만 다르고 밀도 값은 같아야 한다.
 * index.css 에는 토큰으로 표현할 수 없는 것(색·배경·액센트)만 남긴다.
 */
import type { ThemeConfig } from 'antd';
import { brand, gray, state, font, radius, shadow, color, space, layout, density } from './tokens';

export { brand, gray, state, font, radius, shadow, color, space, layout, density, tokens } from './tokens';

export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: brand.primary,
    colorInfo: brand.primary,
    colorLink: brand.primaryActive,
    colorSuccess: state.success,
    colorWarning: state.warning,
    colorError: state.error,

    fontSize: density.fontSize,
    lineHeight: density.lineHeight,
    fontFamily: font.family,
    fontFamilyCode: font.mono,   // antd 기본 코드 글꼴은 리눅스·윈도에서 Courier 로 떨어진다 — 프로젝트 모노 스택으로 통일 (2026-11-04)

    controlHeight: density.controlHeight,
    controlHeightSM: density.controlHeightSM,

    borderRadius: radius.sm,
    borderRadiusLG: radius.md,
    borderRadiusSM: 4,

    padding: space.base,
    paddingLG: space.lg,

    colorBgContainer: color.bg,
    colorBgLayout: color.bgLayout,
    colorBgElevated: color.bgElevated,

    colorBorder: color.border,
    colorBorderSecondary: color.borderSubtle,

    colorText: color.text,
    colorTextSecondary: color.textSecondary,
    colorTextTertiary: color.textTertiary,
    colorTextQuaternary: gray[300],

    boxShadow: shadow.md,
    boxShadowSecondary: shadow.lg,
  },
  components: {
    Layout: {
      headerHeight: layout.headerHeight,
      headerPadding: '0 14px',
    },
    Card: {
      headerBg: color.bg,
      headerHeight: density.cardHeaderHeight,
      headerHeightSM: 28,
      headerPadding: density.cardHeaderPadding,
      headerFontSize: density.fontSize,
      headerFontSizeSM: density.fontSizeSmall,
      bodyPadding: density.cardBodyPadding,
      bodyPaddingSM: 8,
      paddingLG: space.lg,
    },
    Table: {
      headerBg: color.bgLayout,
      headerColor: state.warningText,
      rowHoverBg: color.bgLayout,
      borderColor: color.borderSubtle,
      /* 세 크기(기본/MD/SM)를 모두 건다 — SM 만 걸면 size 를 지정하지 않은 표가
         antd 기본(12~16px)으로 되돌아가 행이 31 → 47px 로 역행한다
         (2026-09-03 로컬 빌드 실측에서 ADB 데모·점검일지·파트너 시험일정이 실제로 잡혔다) */
      cellPaddingBlock: density.tableCellPaddingBlock,
      cellPaddingBlockMD: density.tableCellPaddingBlock,
      cellPaddingBlockSM: density.tableCellPaddingBlock,
      cellPaddingInline: density.tableCellPaddingInline,
      cellPaddingInlineMD: density.tableCellPaddingInline,
      cellPaddingInlineSM: density.tableCellPaddingInline,
      cellFontSize: density.fontSize,
      cellFontSizeMD: density.fontSize,
      cellFontSizeSM: density.fontSize,
    },
    Menu: {
      darkItemBg: 'transparent',
      darkItemSelectedBg: 'rgba(245,158,11,0.25)',
      darkItemSelectedColor: '#ffffff',
      darkItemHoverBg: 'rgba(255,255,255,0.08)',
      darkItemHoverColor: '#ffffff',
      darkItemColor: 'rgba(253,230,138,0.65)',
      darkSubMenuItemBg: 'transparent',
      itemSelectedBg: 'rgba(245,158,11,0.08)',
      itemSelectedColor: brand.primaryActive,
      itemHoverBg: 'rgba(245,158,11,0.04)',
      itemHoverColor: brand.primaryActive,
      itemHeight: density.menuItemHeight,
      itemMarginBlock: density.menuItemMarginBlock,
      itemMarginInline: density.menuItemMarginInline,
      itemPaddingInline: density.menuItemPaddingInline,
      itemBorderRadius: radius.sm,
      iconSize: density.menuIconSize,
      collapsedIconSize: 16,
      collapsedWidth: layout.sidebarCollapsed,
      groupTitleFontSize: 10,
    },
    Button: {
      primaryShadow: brand.primaryShadow,
      defaultBorderColor: color.border,
      paddingInline: 10,
    },
    Input: {
      activeBorderColor: brand.primary,
      hoverBorderColor: brand.primaryHover,
      activeShadow: `0 0 0 3px ${brand.primaryRing}`,
    },
    Form: {
      labelColor: state.warningText,
      labelFontSize: font.size.sm,
      labelHeight: density.formLabelHeight,
      itemMarginBottom: density.formItemMarginBottom,
      verticalLabelPadding: '0 0 2px',
    },
    Tabs: {
      inkBarColor: brand.primary,
      itemActiveColor: brand.primaryActive,
      itemSelectedColor: brand.primaryActive,
      itemHoverColor: brand.primary,
      titleFontSize: density.fontSize,
      horizontalItemPadding: '5px 4px',
      horizontalMargin: '0 0 6px 0',
      cardHeight: 30,
      cardPadding: '4px 12px',
    },
    Modal: {
      /* antd 6 의 Modal 공개 토큰은 색·제목뿐이다 — 본문 패딩은 index.css 에서 정한다 */
      titleFontSize: 15,
      titleLineHeight: 1.4,
    },
    Descriptions: {
      labelBg: color.bgLayout,
      itemPaddingBottom: 6,
    },
    Pagination: {
      itemSize: 24,
    },
    Segmented: {
      trackPadding: 2,
    },
    List: {
      itemPadding: '8px 0',
      itemPaddingSM: '6px 0',
    },
  },
};
